import { Component, useEffect, useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AppstoreOutlined, AuditOutlined, CalculatorOutlined, BankOutlined, CloudDownloadOutlined, DoubleLeftOutlined, DoubleRightOutlined, FileTextOutlined, IdcardOutlined, LaptopOutlined, LogoutOutlined, MailOutlined, MenuOutlined, NotificationOutlined, SettingOutlined, TagsOutlined, TeamOutlined, WalletOutlined } from '@ant-design/icons';
import { Avatar, Button, Drawer, Dropdown, Grid, Layout, Menu, Tooltip } from 'antd';
import { api, apiUrl, getToken, showMobile, signOut as endSession } from './api';
import { Bone } from './components';
import { initials } from './lib';
import { AccountsPage } from './pages/AccountsPage';
import { AccountProfilePage } from './pages/AccountProfilePage';
import { AuditPage } from './pages/AuditPage';
import { CalculationsPage } from './pages/CalculationsPage';
import { DevicesPage } from './pages/DevicesPage';
import { DesktopReleasesPage } from './pages/DesktopReleasesPage';
import { ReleaseNotesPage } from './pages/ReleaseNotesPage';
import { EnquiriesPage } from './pages/EnquiriesPage';
import { GstPage } from './pages/GstPage';
import { LoginPage } from './pages/LoginPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { OverviewPage } from './pages/OverviewPage';
import { PaymentsPage } from './pages/PaymentsPage';
import { PlansPage } from './pages/PlansPage';
import { SETTING_TITLES, SettingsPage } from './pages/SettingsPage';
import { SubscriptionsPage } from './pages/SubscriptionsPage';
const { Sider, Header, Content } = Layout;
const PAGES = {
    overview: { title: 'Overview', section: 'Workspace', icon: <AppstoreOutlined />, render: (go) => <OverviewPage go={go}/> },
    // /accounts/<id> is one account's profile.
    accounts: { title: 'Accounts', section: 'Customers', icon: <TeamOutlined />, render: (go, sub) => (sub ? <AccountProfilePage key={sub} id={sub} go={go}/> : <AccountsPage go={go}/>) },
    subscriptions: { title: 'Subscriptions', section: 'Customers', icon: <IdcardOutlined />, render: () => <SubscriptionsPage /> },
    devices: { title: 'Devices', section: 'Customers', icon: <LaptopOutlined />, render: () => <DevicesPage /> },
    plans: { title: 'Plans', section: 'Billing', icon: <TagsOutlined />, render: () => <PlansPage /> },
    payments: { title: 'Payments', section: 'Billing', icon: <WalletOutlined />, render: () => <PaymentsPage /> },
    gst: { title: 'GST', section: 'Platform', icon: <BankOutlined />, render: (go, sub) => <GstPage section={sub} go={(next) => go('gst', next)}/> },
    calculations: { title: 'Calculation audit', section: 'Platform', icon: <CalculatorOutlined />, render: () => <CalculationsPage /> },
    notifications: { title: 'Updates', section: 'Platform', icon: <NotificationOutlined />, render: () => <NotificationsPage /> },
    releases: { title: 'Desktop releases', section: 'Platform', icon: <CloudDownloadOutlined />, render: () => <DesktopReleasesPage /> },
    'release-notes': { title: 'Release notes', section: 'Platform', icon: <FileTextOutlined />, render: () => <ReleaseNotesPage /> },
    enquiries: { title: 'Contact enquiries', section: 'Platform', icon: <MailOutlined />, render: () => <EnquiriesPage /> },
    audit: { title: 'Audit log', section: 'System', icon: <AuditOutlined />, render: () => <AuditPage /> },
    settings: { title: 'Settings', section: 'System', icon: <SettingOutlined />, render: (go, sub) => <SettingsPage go={go} section={sub}/> }
};
const SECTIONS = ['Workspace', 'Customers', 'Billing', 'Platform', 'System'];
/** The page in the address (/accounts, /settings/storage), so a reload or a shared link opens it again. */
function routeOf(pathname) {
    const [, name = '', sub = ''] = pathname.split('/');
    return name in PAGES ? { page: name, sub } : { page: 'overview', sub: '' };
}
/** One page of the console, with the part of it named in the address (a settings group). */
function PageRoute({ page, go }) {
    const { sub = '' } = useParams();
    return <>{PAGES[page].render(go, sub)}</>;
}
const SIDER_KEY = 'kivonex-admin-sider-collapsed';
/** Whether the sidebar was left collapsed in this browser (a convenience only, so storage may be missing). */
function keptCollapsed() {
    try {
        return localStorage.getItem(SIDER_KEY) === '1';
    }
    catch {
        return false;
    }
}
function keepCollapsed(value) {
    try {
        localStorage.setItem(SIDER_KEY, value ? '1' : '0');
    }
    catch {
        // Private window or blocked storage: the sidebar just opens expanded next time.
    }
}
const NAV = SECTIONS.map((section) => ({
    type: 'group',
    key: section,
    label: section,
    children: Object.keys(PAGES)
        .filter((key) => PAGES[key].section === section)
        .map((key) => ({ key, icon: PAGES[key].icon, label: PAGES[key].title }))
}));
function AdminApp() {
    const [token, setSignedIn] = useState(getToken());
    const navigate = useNavigate();
    const { page, sub } = routeOf(useLocation().pathname);
    const [menuOpen, setMenuOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(keptCollapsed);
    const [apiUp, setApiUp] = useState(null);
    const [me, setMe] = useState(null);
    const screens = Grid.useBreakpoint();
    const compact = !screens.lg;
    useEffect(() => {
        const onUnauthorized = () => setSignedIn(null);
        window.addEventListener('kivonex-admin-unauthorized', onUnauthorized);
        return () => window.removeEventListener('kivonex-admin-unauthorized', onUnauthorized);
    }, []);
    useEffect(() => {
        if (!token)
            return setMe(null);
        api('/v1/admin/me').then((body) => setMe(body.admin)).catch(() => undefined);
    }, [token]);
    useEffect(() => {
        if (!token)
            return;
        const check = () => fetch(apiUrl('/healthz')).then((response) => setApiUp(response.ok)).catch(() => setApiUp(false));
        void check();
        const timer = window.setInterval(check, 30000);
        return () => window.clearInterval(timer);
    }, [token]);
    useEffect(() => {
        // ⌘B / Ctrl+B collapses or expands the sidebar.
        const onKey = (event) => {
            if ((event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey && event.key.toLowerCase() === 'b') {
                event.preventDefault();
                setCollapsed((value) => {
                    keepCollapsed(!value);
                    return !value;
                });
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);
    useEffect(() => {
        document.title = `${(page === 'settings' && SETTING_TITLES[sub]) || PAGES[page].title} · Kivonex Admin`;
    }, [page, sub]);
    if (!token)
        return <LoginPage onSignedIn={(value) => setSignedIn(value)}/>;
    const go = (next, nextSub = '') => {
        navigate(nextSub ? `/${next}/${nextSub}` : `/${next}`);
        setMenuOpen(false);
        window.scrollTo({ top: 0 });
    };
    const signOut = async () => {
        await endSession();
        setSignedIn(null);
    };
    const toggleSider = () => {
        keepCollapsed(!collapsed);
        setCollapsed(!collapsed);
    };
    const apiLabel = apiUp === false ? 'API unreachable' : apiUp === null ? 'Checking API…' : 'API online';
    /** The sidebar's contents; `rail` is the collapsed, icons-only form (never in the phone drawer). */
    const nav = (rail) => (<>
      <div className="brand">
        <span className="brand-mark" aria-hidden>K</span>
        <span className="brand-name">
          Kivonex
          <small>Admin console</small>
        </span>
      </div>
      <Menu className="shell-nav" theme="dark" mode="inline" inlineCollapsed={rail} selectedKeys={[page]} items={NAV} onClick={({ key }) => go(key)}/>
      <div className="sider-foot">
        <Tooltip placement="right" title={rail ? `${apiLabel} · Kivonex Cloud API` : ''}>
          <div className="sider-status">
            <span className={apiUp === false ? 'status-dot off' : apiUp === null ? 'status-dot wait' : 'status-dot'}/>
            <span className="sider-status-text">
              <strong>{apiLabel}</strong>
              Kivonex Cloud API
            </span>
          </div>
        </Tooltip>
        {!compact && (<Tooltip placement="right" title={rail ? 'Expand sidebar (⌘B)' : ''}>
            <button type="button" className="sider-toggle" onClick={toggleSider} aria-label={rail ? 'Expand sidebar' : 'Collapse sidebar'} aria-expanded={!rail}>
              {rail ? <DoubleRightOutlined /> : <DoubleLeftOutlined />}
              <span className="sider-toggle-text">Collapse</span>
              <kbd>⌘B</kbd>
            </button>
          </Tooltip>)}
      </div>
    </>);
    return (<Layout className="shell">
      {!compact && (<Sider width={256} collapsedWidth={76} collapsed={collapsed} trigger={null} className={collapsed ? 'shell-sider is-rail' : 'shell-sider'}>
          {nav(collapsed)}
        </Sider>)}
      {compact && (<Drawer placement="left" open={menuOpen} onClose={() => setMenuOpen(false)} size={264} closable={false} styles={{ body: { padding: 0, background: 'var(--sider)', display: 'flex', flexDirection: 'column' } }} rootClassName="shell-drawer">
          {nav(false)}
        </Drawer>)}
      <Layout>
        <Header className="topbar">
          <div className="topbar-left">
            {compact && <Button type="text" icon={<MenuOutlined />} onClick={() => setMenuOpen(true)} aria-label="Open menu"/>}
            <span className="crumbs">
              {PAGES[page].section} /{' '}
              {page === 'settings' && SETTING_TITLES[sub] ? (<>
                  <Link to="/settings">Settings</Link> / <b>{SETTING_TITLES[sub]}</b>
                </>) : page === 'accounts' && sub ? (<>
                  <Link to="/accounts">Accounts</Link> / <b>Profile</b>
                </>) : (<b>{PAGES[page].title}</b>)}
            </span>
          </div>
          <Dropdown trigger={['click']} menu={{
            items: [
                { key: 'account', icon: <SettingOutlined />, label: 'Admin users' },
                { type: 'divider' },
                { key: 'out', icon: <LogoutOutlined />, label: 'Sign out', danger: true }
            ],
            onClick: ({ key }) => (key === 'out' ? void signOut() : go('settings', 'admins'))
        }}>
            <div className="user-chip" role="button" tabIndex={0}>
              <Avatar size={32} style={{ background: 'var(--brand)', fontWeight: 600 }}>{me ? initials(me.name) : 'A'}</Avatar>
              <span className="user-chip-text">
                {me ? (<>
                    {me.name}
                    <small>{showMobile(me.mobile)}</small>
                  </>) : (<span className="bone-lines" style={{ gap: 6 }} aria-busy>
                    <Bone width={84} height={11}/>
                    <Bone width={110} height={9}/>
                  </span>)}
              </span>
            </div>
          </Dropdown>
        </Header>
        <Content className="content">
          <Routes>
            {Object.keys(PAGES).map((key) => (<Route key={key} path={`/${key}`} element={<PageRoute page={key} go={go}/>}/>))}
            <Route path="/settings/:sub" element={<PageRoute page="settings" go={go}/>}/>
            <Route path="/accounts/:sub" element={<PageRoute page="accounts" go={go}/>}/>
            <Route path="/gst/:sub" element={<PageRoute page="gst" go={go}/>}/>
            <Route path="*" element={<Navigate to="/overview" replace/>}/>
          </Routes>
        </Content>
      </Layout>
    </Layout>);
}

class AdminErrorBoundary extends Component {
    state = { error: null };
    static getDerivedStateFromError(error) {
        const message = error instanceof Error ? error.message : typeof error === 'string' ? error : error?.message || JSON.stringify(error) || 'Unexpected interface error';
        return { error: message };
    }
    componentDidCatch(error, info) {
        console.error('Kivonex ADMIN interface error', error, info);
    }
    render() {
        if (!this.state.error)
            return this.props.children;
        return <div className="fatal-error"><div><h1>ADMIN could not show this screen</h1><p>{this.state.error}</p><Button type="primary" onClick={() => window.location.reload()}>Reload ADMIN</Button></div></div>;
    }
}

export function App() {
    return <AdminErrorBoundary><AdminApp /></AdminErrorBoundary>;
}
